import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';

import { useMixpanel } from 'v3/hooks/useMixpanel';
import { ContractFlowClient } from 'v3/api/client/ContractFlowClient';
import { handleErrorMessage } from 'v3/utils/errorHandler';

import SidebarBox from 'v3/components/SidebarBox';
import { CTA_SLOT_ID } from './components/cta-slot';
import ContractBox from 'v3/components/ContractBox';
import Signature, { SignatureElement } from 'v3/components/Signature';
import Steps from 'v3/components/Steps';
import FlowScreen from 'v3/components/FlowScreen';


import * as S from './styles';

import { useExperiment } from '@marvelapp/react-ab-test';
// import { useIntercom } from 'hooks/useIntercom';
import PaymentBox from 'v3/components/PaymentBox';
import PaymentMethod from 'components/PaymentMethod';
import { ACHForm } from './components/ach';
import { setPlaidTrigger } from 'leadData/plaidSlice';
import { CCForm } from './components/credit-card';
import { useQuery } from 'v3/hooks/useQuery';
import { toast } from 'react-toastify';
import smoothscroll from 'smoothscroll-polyfill';
import { is } from 'date-fns/locale';
import { get } from 'http';
import { setLeadData } from 'leadData/leadSlice';
import LoadingOverlay from './components/LoadingOverlay';
import * as Sentry from "@sentry/react";
import { UsdFormatter } from 'v3/utils/formatNumber';
import { PaymentClient } from 'v3/api/client/PaymentClient';

// import MinimalBanner from 'v3/components/MinimalBanner';
// import VideoModal from 'v3/components/VideoModal';

declare global {
  interface Window {
    _fillAllInitialsGaps?: () => void;
  }
}

const SignDeal = () => {
  const cfUuid = useQuery().get('cfUuid') || undefined;
  const { leadData } = useSelector((state: RootState) => state.lead);
    const [range, setRange] = useState<[number, number]>([1, 1]);
  const [isDrawingValid, setIsDrawingValid] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isContractLoaded, setIsContractLoaded] = useState(false);
  const [signContractError, setSignContractError] = useState<boolean>(false);
  // const [activateIntercom, setActivateIntercom] = useState(false);
  const [halfPayment, setHalfPayment] = useState(0);
  const [clientSecret, setClientSecret] = useState('');

  const contractIframeRef = useRef<HTMLIFrameElement>(null);
  const signatureRef = useRef<SignatureElement>(null);
  const signatureSectionRef = useRef<HTMLHeadingElement>(null);
  const [contractBody, setContractBody] = useState<HTMLBodyElement | null>(null);
  const [remainingInitials, setRemainingInitials] = useState(0);
  const [totalInitials, setTotalInitials] = useState(0);

  const mp = useMixpanel();
  const { selectVariant } = useExperiment('Plaid On Exit');
  const showPromotionalModal = selectVariant({
    A: true,
    B: false,
  });
  // const [openVideoModal, setOpenVideoModal] = useState(false);

  const [paymentMethod, setPaymentMethod] =
    useState<'ach' | 'credit-card'>('credit-card');

  const dispatch = useDispatch();
  // useIntercom(activateIntercom);

  smoothscroll.polyfill();

  const isMobileDevice = (): boolean => {
    const userAgent = navigator.userAgent;
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  }

  const getMemeberInitials = useCallback(() => {
    return contractBody?.querySelectorAll(".member-initials:not(.checked)");
  }, [contractBody])


  const getAllMemeberInitials = useCallback(() => {
    return contractBody?.querySelectorAll(".member-initials");
  }, [contractBody])


  const iframeScrollTo = useCallback((element: HTMLElement) => {
    const iframeWindow = contractIframeRef.current?.contentWindow;
    if (iframeWindow) {

      const elementRect = element.getBoundingClientRect();
      const iframeHeight = iframeWindow.innerHeight;
      const targetY = elementRect.top + iframeWindow.pageYOffset - (iframeHeight / 2) + (elementRect.height / 2);

      iframeWindow.scrollTo({
          top: targetY,
          behavior: 'smooth'
      });
    } else {
        console.warn("Iframe or target element not found.");
    }
  }, [contractIframeRef, contractBody]);


  const goToNextInitialsGap = useCallback((initials: NodeListOf<Element> | undefined = undefined) => {
    if(!initials || initials.length === 0) {
      initials = getMemeberInitials();
    }

    if(initials && initials.length > 0) {
      iframeScrollTo(initials[0] as HTMLElement);
    }
    else {
      if (signatureSectionRef.current)
      {
        const signatureEl = signatureSectionRef.current;
        signatureEl?.scrollIntoView({
          behavior: 'smooth',
          block: "center",
          inline: "center"
        });
      }
    }
  }, [getMemeberInitials]);


  const validateMemeberInitialsFilling = useCallback(() => {
    const initials = getMemeberInitials();

    const valid = initials?.length === 0
    if(!valid) {
      toast.warning("Please fill all member initials in the contract agreement");
      goToNextInitialsGap(initials)
    }
    return valid
  }, [getMemeberInitials, goToNextInitialsGap]);


  const handleClickAddInitials = useCallback((el: Element) => {
    // Build initials safely: a legal entity has no first/last name, so never index an
    // empty string. Drop the "LLC" suffix and use the first and last remaining parts.
    const nameParts = `${leadData.firstName ?? ''} ${leadData.lastName ?? ''}`
      .split(/\s+/)
      .filter((part) => part && part.toUpperCase() !== 'LLC');
    const initials = [nameParts[0], ...nameParts.slice(1).slice(-1)]
      .filter(Boolean)
      .map((part) => part[0].toUpperCase())
      .join(' ');
    const signGap = el.querySelector(".sign-gap");
    if(signGap)
    {
        signGap.innerHTML = initials;
        el.classList.add('checked');

        const remaining = getMemeberInitials()?.length || 0;
        const allInitials = getAllMemeberInitials()?.length || 0;
        const gapsFilled = allInitials - remaining;

        setRemainingInitials(gapsFilled);
        goToNextInitialsGap();
    }
  }, [leadData.firstName, leadData.lastName, getMemeberInitials, goToNextInitialsGap]);


  const engageClickAddInitials = useCallback(() => {

    const goToNextInitialsButton = contractBody?.querySelector('.go-to-next-initials');
    if(goToNextInitialsButton) {
      goToNextInitialsButton.addEventListener("click", () => {
        goToNextInitialsGap();
      });
    }
    const memberInitials: NodeListOf<Element> | undefined = getMemeberInitials();

    setTotalInitials(memberInitials?.length || 0);

    memberInitials?.forEach((el, index) => {
      el.setAttribute("id", `member-initials-${index}`);
      const addInitialsButton = el.querySelector("button");
      addInitialsButton?.addEventListener('click', () => {
        handleClickAddInitials(el);
      });
    })
  }, [getMemeberInitials, handleClickAddInitials]);


  const _fillAllInitialsGaps = useCallback(() => {
    const memberInitials: NodeListOf<Element> | undefined = getMemeberInitials();

    memberInitials?.forEach((el) => {
      handleClickAddInitials(el);
    })

  }, [getMemeberInitials, handleClickAddInitials]);

  window._fillAllInitialsGaps = _fillAllInitialsGaps;

  const loadContract = useCallback(async () => {
    if(contractIframeRef.current) {

      setContractBody(contractIframeRef.current.contentDocument?.body as HTMLBodyElement);

      if(cfUuid) {
        const { data, error } = await ContractFlowClient().getContractDocument(cfUuid);
        if (data) {
          if(contractBody) {
            contractBody.innerHTML = data;
            engageClickAddInitials();
            setIsContractLoaded(true);
          }
        }
        if (error) {
          handleErrorMessage(error);
        }
      }
    }

  }, [contractIframeRef, contractBody]);


  useEffect(() => {
    loadContract();
  }, [loadContract]);

  useEffect(() => {
    if(isContractLoaded && leadData.contractSigned) {
      _fillAllInitialsGaps();
      setRemainingInitials(totalInitials);
      setIsDrawingValid(true);
      // toast.info("Contract already signed. You can proceed to payment.");
    }
  }, [isContractLoaded, leadData.contractSigned]);

  useEffect(() => {
    const getHalfPayment = async () => {
      const { data } = await ContractFlowClient().getHalfPayment(
        leadData.contractFlowManagementFee,
        leadData.weeklyCycle
          ? leadData.contractFlowWeeklyPayment
          : leadData.contractFlowMonthlyPayment
      );

      if (data) {
        setHalfPayment(data.data.halfPayment);
      }
    };

    if (leadData.contractFlowHalfPaymentSelected) {
      getHalfPayment();
    }
  }, [
    leadData.contractFlowManagementFee,
    leadData.contractFlowMonthlyPayment,
    leadData.contractFlowHalfPaymentSelected,
  ]);

  useEffect(() => {
    mp.fireAccessSignDeal();
  }, [mp]);

  const TooltipText = (paymentType: string) => {
    if (paymentType === 'half') {
      return '*The number of months to get the car may vary depending on a lot of factors, value of club, number of members inside the club etc';
    } else {
      return 'With good payment habits, you could receive your car within the first 30 months. Everyone receives their car by the end of the contract term if all payments have been made.';
    }
  };

  const ChoosePaymentMethod = () => {
    return (
      <>
        {isLoading && <LoadingOverlay />}
        <S.PaymentMethodSelection>
          <PaymentMethod
            title="Pay with Credit Card"
            type="credit-card"
            selected={paymentMethod === 'credit-card'}
            onClick={() => setPaymentMethod('credit-card')}
            />

          <PaymentMethod
            title="Pay with ACH"
            type="ach"
            selected={paymentMethod === 'ach'}
            onClick={() => setPaymentMethod('ach')}
            />
        </S.PaymentMethodSelection>
      </>
    );
  };

  /**
   * Re-read the application and move on if it turns out to be signed already.
   *
   * The step is otherwise only read once, when the app mounts, so a sign that succeeded on
   * the server after the client stopped waiting leaves the customer stuck on a step they
   * have in fact completed. Returns whether the customer was moved on.
   */
  const refetchContractFlow = useCallback(async () => {
    const [data] = await ContractFlowClient().getContractFlowInfo(leadData.contractFlowToken);

    if (!data) return false;

    const { contractFlowStep, ...rest } = data;
    if (contractFlowStep <= 2) return false;

    dispatch(setLeadData({ leadData: rest, currentStep: contractFlowStep }));
    return true;
  }, [leadData.contractFlowToken, dispatch]);

  const signContract = useCallback(async (paymentParams: { stripeToken?: string; plaidAccountId?: string; plaidPublicToken?: string }) => {
    const signature = signatureRef.current?.getBase64Image()

    if(signature && isDrawingValid) {
      setIsLoading(true);
      const { data, error } = await ContractFlowClient().signContract(
        leadData.contractFlowToken,
        signature,
        paymentParams,
      );

      if(error) {
        setIsLoading(false)
        setSignContractError(true);
        let errorMessage = error?.detail as string || "";

        if (errorMessage) { // Known API error with a user-facing message
          errorMessage = errorMessage.replace(/Request.+: /g, "");
          toast.error(errorMessage);
        }
        else {
          Sentry.setContext("Contract Data", leadData);
          Sentry.captureException(new Error(`Sign contract failed: ${JSON.stringify(error)}`));
          console.error('Error callback signing contract:', error);

          // A timed-out request has no server error to quote, and the server may well have
          // finished signing after we gave up. Re-read the application: if it is signed, the
          // customer is done and belongs on the next step rather than looking at an error.
          const recovered = await refetchContractFlow();
          if (recovered) return;

          toast.error('We could not confirm your signature. Please check your connection and try again.');
        }

        throw new Error(`Error signing contract: ${errorMessage || 'no response from server'}`);
      }

      if (data) {
        setIsLoading(false);
        dispatch(
          setLeadData({
            leadData: data,
            currentStep: data.contractFlowStep,
          })
        );
      }
    }
  }, [signatureRef, isDrawingValid, leadData.contractFlowToken, refetchContractFlow])

  useEffect(() => {
      async function setupPaymentIntent() {
        if (!cfUuid) return;

        const {data, error} = await PaymentClient().setupPaymentIntent(cfUuid);
        if (error) {
          toast.error('Error setting up payment intent');
          console.error('Error setting up payment intent:', error);
          return;
        }

        setClientSecret(data?.clientSecret || '');
      }
      setupPaymentIntent();
  }, [cfUuid, signContractError]);

  return (
    <>
      {/* <VideoModal
          title={
            <>
              How do payments
              <br />
              works?
            </>
          }
          videoLink="https://www.youtube.com/embed/BG99AdK_gBk"
          videoType="youtube"
          startAt={100}
          closeModal={() => setOpenVideoModal(false)}
          open={openVideoModal}
          backgroundImage={modalBackgroundImage}
        /> */}
      <FlowScreen>
        <S.Wrapper>
          <S.Col xs={24} lg={24} xl={24}>
          {/* Eyebrow outside the card, title inside. */}
          <S.PayEyebrow>
            <span className="phase">Contract</span>
            <span className="rest"> · Step 3 of 3</span>
          </S.PayEyebrow>
          <S.PayCardShell>
          <SidebarBox variant="secondary">
            <S.PayTitle>Sign &amp; make it official</S.PayTitle>
            <S.PaySubtitle>
              Review your plan, sign, and set up your first contribution
            </S.PaySubtitle>

            <S.ContractAgreementSection id="agreement">
              {/* Numbered sections: the signature is part of the agreement, so
                  only the two top-level blocks carry a number. */}
              <S.SectionTitle>
                <span className="step">1.</span> Contract agreement
              </S.SectionTitle>
              <S.Paragraph>
                Please review the contract agreement below and fill all the gaps with your initials.
                By doing this we'll know that you are aware and have understood the contract.
              </S.Paragraph>

              <ContractBox isContractLoaded={isContractLoaded}>
                <iframe
                  ref={contractIframeRef}
                  name="contract"
                  height="100%"
                  width="100%"
                />
              </ContractBox>
              <S.Badge>Initials gaps filled {remainingInitials} of {totalInitials}</S.Badge>
            </S.ContractAgreementSection>

            <S.SignatureSection>
              <S.SectionTitle ref={signatureSectionRef}>Signature</S.SectionTitle>
              <Signature
                setIsDrawingValid={setIsDrawingValid}
                disabled={isLoading || leadData.contractSigned}
                ref={signatureRef}
                name={`${leadData.firstName} ${leadData.lastName}`}
                description={
                  <S.SignatureDescription>
                    By signing this document with an eletronic signature, i
                    agree that such signature <br />
                    will be as valid as a handwritten signatures to the extent
                    allowed by local law.
                  </S.SignatureDescription>
                }
              />
            </S.SignatureSection>

            <S.SectionTitle>
              <span className="step">2.</span> Payment method
            </S.SectionTitle>

            <S.PaymentMethodSection>
              <PaymentBox
                weeklyCycle={leadData.weeklyCycle}
                weekTerm={Number(leadData.weekTerm)}
                weeklyPayment={leadData.contractFlowWeeklyPayment}
                monthlyPayment={leadData.contractFlowMonthlyPayment}
                halfPayment={halfPayment}
                paymentOption={
                  leadData.contractFlowHalfPaymentSelected ? 'half' : 'full'
                }
                term={Number(leadData.term)}
                downPayment={leadData.contractFlowDownPayment}
                firstPayment={leadData.contractFlowFirstPayment ?? undefined}
              >
                <S.PaymentMethodChildren>
                  {/* <MinimalBanner
                      variant="secondary"
                      title="To finalized your personalized deal"
                      text={
                        <p>
                          Connect with Plaid and fill your bank account
                          <br />
                          information to make your first payment on debit.
                        </p>
                      }
                      onClick={() => setOpenVideoModal(true)}
                    /> */}
                </S.PaymentMethodChildren>
              </PaymentBox>


              <S.ChoosePaymentMethodWrapper>
                <S.ChoosePaymentMethodSection>
                  <ChoosePaymentMethod />
                </S.ChoosePaymentMethodSection>
                {/* Rendered directly, never through a wrapper defined in this render
                    body: such a wrapper is a new function on every render, so React
                    treats it as a different component type and remounts the subtree.
                    That destroyed Stripe's CardElement — filling an initials gap calls
                    setRemainingInitials, and the member's typed card vanished. */}
                {paymentMethod === 'ach' ? (
                  <ACHForm
                    isDrawingValid={isDrawingValid}
                    signContract={signContract}
                    validateOnSubmit={validateMemeberInitialsFilling}
                  />
                ) : (
                  <CCForm
                    isDrawingValid={isDrawingValid}
                    signContract={signContract}
                    validateOnSubmit={validateMemeberInitialsFilling}
                    clientSecret={clientSecret}
                    customerData={leadData}
                  />
                )}
              </S.ChoosePaymentMethodWrapper>
            </S.PaymentMethodSection>
          </SidebarBox>
          </S.PayCardShell>

          {/* CTA target: the ACH and card forms portal the sign button in here, so
              it sits outside the card as on Getting Started. */}
            <S.CtaSlot id={CTA_SLOT_ID} />
          </S.Col>
        </S.Wrapper>
      </FlowScreen>
    </>
  );
};

export default SignDeal;
